// ============================================================
// TEXTOS DEL SITIO EN INGLÉS Y ESPAÑOL
// ------------------------------------------------------------
// El inglés es el idioma por defecto. Si el navegador del
// visitante está en español, el sitio cambia solo a español.
// ============================================================

export type Lang = 'en' | 'es'

export const copy = {
  en: {
    langName: 'English',
    langShort: 'EN',
    switchTo: 'Ver en español',
    languageLabel: 'Language',

    header: {
      searchPlaceholder: 'Search model or VIN',
      searchOpen: 'Search inventory',
      searchClose: 'Close search',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      directions: 'Get directions to the dealership',
      account: 'My account',
      call: 'Call',
    },

    mobileBar: {
      call: 'Call',
      whatsapp: 'WhatsApp',
      quote: 'Get a Quote',
    },

    nav: {
      new: 'New Vehicles',
      preOwned: 'Pre-Owned',
      service: 'Service & Parts',
      finance: 'Financing',
      contact: 'Contact',
    },

    hero: {
      eyebrow: 'Porsche North Miami',
      title: 'Precision, Now in Paradise.',
      subtitle:
        'Explore the full range of new and certified Porsche models in South Florida.',
      ctaPrimary: 'View New Inventory',
      ctaSecondary: 'Schedule a Test Drive',
      metaOfficial: 'Official Dealership',
      scroll: 'Scroll',
      scrollAria: 'Scroll to categories',
    },

    categories: {
      eyebrow: 'Dealership',
      title: 'Everything Porsche, in one place',
      link: 'View full inventory',
      items: {
        nuevos: { title: 'New Vehicles', meta: '84 available' },
        'pre-owned': { title: 'Certified Pre-Owned', meta: '46 available' },
        servicio: { title: 'Service & Parts', meta: 'Certified technicians' },
        tequipment: { title: 'Tequipment Accessories', meta: 'Factory original' },
      },
    },

    lineup: {
      eyebrow: '2026 Inventory',
      title: 'The full lineup, in North Miami',
      filterAria: 'Filter by segment',
      disclaimer:
        'Suggested MSRP. Excludes taxes, title, registration and destination charge. Estimated lease payments based on 36 months / 7,500 miles per year with approved credit. Subject to availability.',
    },

    filters: {
      all: 'All',
      Deportivos: 'Sports Cars',
      SUV: 'SUV',
      Eléctricos: 'Electric',
      Sedanes: 'Sedans',
    },

    card: {
      from: 'From',
      lease: 'Est. lease',
      perMonth: '/mo',
      cta: 'View Details',
      alt: (model: string, paint: string) => `Porsche ${model} in ${paint}`,
    },

    trims: {
      '911-carrera': 'Coupé · 379 hp · 8-speed PDK',
      taycan: '100% electric · 800V fast charging',
      cayenne: 'SUV · 348 hp · All-wheel drive',
      macan: 'Compact SUV · 261 hp',
      panamera: 'Sport sedan · 348 hp',
      '911-certificado': 'Certified Pre-Owned · 12,400 mi',
    },

    badges: {
      'New Arrival': 'New Arrival',
      Electric: 'Electric',
      Certified: 'Certified',
      Oferta: 'Special Offer',
    },

    service: {
      eyebrow: 'After-sales',
      title: 'Certified Service Center',
      caption: 'Certified Workshop — North Miami',
      imageAlt: 'Certified technician working on a Porsche inside the workshop',
      cta: 'Schedule Service',
      highlights: [
        {
          title: 'Porsche-certified technicians',
          description:
            'PIWIS diagnostics and genuine parts, with factory warranty on every job.',
        },
        {
          title: 'Express service and courtesy vehicle',
          description:
            'Same-day scheduled maintenance, with pickup and delivery across Miami-Dade County.',
        },
      ],
      banner: {
        eyebrow: 'Certified workshop',
        title: 'Book Technical Service',
        description:
          'Pick your day and time online, with a courtesy vehicle subject to availability and PIWIS diagnostics included.',
        cta: 'Book Technical Service',
      },
    },

    finance: {
      eyebrow: 'Financing',
      title: 'Apply for financing or value your trade-in',
      body: 'Our team structures your plan with Porsche Financial Services and sends you an offer for your current vehicle before you visit the showroom.',
      imageAlt: 'Dealership showroom at dusk',
      steps: [
        { label: 'Online application', detail: 'Answer in under 24 business hours.' },
        { label: 'Trade-in appraisal', detail: 'Firm offer, valid for 7 days.' },
        { label: 'Porsche Financial plans', detail: 'Lease, finance and balloon.' },
      ],
      ctaPrimary: 'Apply for Financing',
      ctaSecondary: 'Value my Trade-In',
      banner: {
        eyebrow: 'Porsche Financial Services',
        title: 'Calculate Your Lease Payment',
        description:
          'Simulate term, down payment and annual mileage to see your estimated monthly payment in minutes.',
        cta: 'Calculate Lease Payment',
      },
    },

    lead: {
      advisorEyebrow: 'Your advisor',
      advisorRole: 'Porsche Certified Sales Advisor',
      advisorTagline:
        'More than 10 years helping South Florida clients buy, lease and trade in their Porsche.',
      responseTime: 'Average reply in under 15 minutes',
      whatsapp: 'Message on WhatsApp',
      eyebrow: 'Contact',
      title: 'Request your personalized quote',
      subtitle: (first: string) =>
        `Tell us which model you have in mind and ${first} sends you availability, final pricing and lease options the same business day.`,
      name: 'Full name',
      namePlaceholder: 'First and last name',
      phone: 'Phone',
      email: 'Email',
      emailPlaceholder: 'you@email.com',
      interest: 'Model of interest',
      timeline: 'When are you planning to buy?',
      message: 'Message (optional)',
      messagePlaceholder: 'Color, option package, trade-in, test drive…',
      submit: 'Request a Quote',
      sending: 'Sending…',
      consent: (name: string, phone: string, email: string) =>
        `By submitting you agree to be contacted by ${name} at ${phone} or ${email}. No purchase obligation.`,
      success: (first: string) =>
        `Thank you, your request was sent. ${first} will contact you shortly.`,
      honeypot: 'Do not fill',
      interests: [
        'Porsche 911 Carrera',
        'Porsche Taycan',
        'Porsche Cayenne',
        'Porsche Macan',
        'Porsche Panamera',
        'Certified Pre-Owned',
        'Service & Parts',
      ],
      timelines: ['As soon as possible', 'Within 30 days', 'In 1 to 3 months', 'Just browsing'],
      whatsappIntro: 'Hello, I would like to request a quote.',
      whatsappFields: {
        name: 'Name',
        phone: 'Phone',
        email: 'Email',
        interest: 'Model of interest',
        timeline: 'Purchase timeline',
        message: 'Message',
      },
    },

    footer: {
      addressLabel: 'Address',
      directions: 'Get directions',
      hoursLabel: 'Hours',
      phoneLabel: 'Phone',
      advisorNote: (name: string, role: string, line: string) =>
        `Direct attention with ${name}, ${role.toLowerCase()}. Dealership line: ${line}.`,
      testDrive: 'Schedule a Test Drive',
      newsletter: 'Newsletter',
      newsletterCopy:
        'New arrivals, limited editions and invitations to dealership events.',
      emailPlaceholder: 'Email address',
      subscribe: 'Subscribe',
      subscribed: 'Done, we will keep you posted.',
      follow: 'Follow us',
      columns: [
        {
          title: 'Sales',
          links: ['New Inventory', 'Certified Pre-Owned', 'Test Drive', 'Special Offers'],
        },
        {
          title: 'After-sales',
          links: ['Schedule Service', 'Genuine Parts', 'Collision & Paint', 'Tequipment'],
        },
        {
          title: 'Dealership',
          links: ['About Us', 'Financing', 'Trade-In', 'Privacy Notice'],
        },
      ],
      legal: ['Privacy Notice', 'Terms of Use', 'Accessibility', 'Cookies'],
      rights: 'Authorized official dealership.',
      hours: [
        { days: 'Sales · Mon – Fri', time: '9:00 AM – 7:00 PM' },
        { days: 'Sales · Saturday', time: '9:00 AM – 6:00 PM' },
        { days: 'Service · Mon – Fri', time: '7:30 AM – 6:00 PM' },
        { days: 'Sunday', time: 'Closed' },
      ],
    },
  },

  es: {
    langName: 'Español',
    langShort: 'ES',
    switchTo: 'View in English',
    languageLabel: 'Idioma',

    header: {
      searchPlaceholder: 'Buscar modelo o VIN',
      searchOpen: 'Buscar inventario',
      searchClose: 'Cerrar búsqueda',
      openMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
      directions: 'Cómo llegar al concesionario',
      account: 'Mi cuenta',
      call: 'Llamar',
    },

    mobileBar: {
      call: 'Llamar',
      whatsapp: 'WhatsApp',
      quote: 'Cotizar',
    },

    nav: {
      new: 'Vehículos Nuevos',
      preOwned: 'Pre-Owned',
      service: 'Servicio y Partes',
      finance: 'Financiamiento',
      contact: 'Contacto',
    },

    hero: {
      eyebrow: 'Porsche North Miami',
      title: 'Precision, Now in Paradise.',
      subtitle:
        'Explora la gama completa de modelos Porsche nuevos y certificados en el sur de la Florida.',
      ctaPrimary: 'Ver Inventario Nuevo',
      ctaSecondary: 'Agendar Prueba de Manejo',
      metaOfficial: 'Concesionario Oficial',
      scroll: 'Scroll',
      scrollAria: 'Bajar a las categorías',
    },

    categories: {
      eyebrow: 'Concesionario',
      title: 'Todo Porsche, en un solo lugar',
      link: 'Ver inventario completo',
      items: {
        nuevos: { title: 'Vehículos Nuevos', meta: '84 disponibles' },
        'pre-owned': { title: 'Pre-Owned Certificados', meta: '46 disponibles' },
        servicio: { title: 'Servicio y Partes', meta: 'Técnicos certificados' },
        tequipment: { title: 'Accesorios Tequipment', meta: 'Originales de fábrica' },
      },
    },

    lineup: {
      eyebrow: 'Inventario 2026',
      title: 'La gama completa, en North Miami',
      filterAria: 'Filtrar por segmento',
      disclaimer:
        'Precios MSRP sugeridos, no incluyen impuestos, título, registro ni cargo de destino. Cuotas de leasing estimadas a 36 meses / 7,500 millas anuales con crédito aprobado. Sujeto a disponibilidad.',
    },

    filters: {
      all: 'Todos',
      Deportivos: 'Deportivos',
      SUV: 'SUV',
      Eléctricos: 'Eléctricos',
      Sedanes: 'Sedanes',
    },

    card: {
      from: 'Desde',
      lease: 'Leasing est.',
      perMonth: '/mes',
      cta: 'Ver Ficha',
      alt: (model: string, paint: string) => `Porsche ${model} en ${paint}`,
    },

    trims: {
      '911-carrera': 'Coupé · 379 hp · PDK 8 vel.',
      taycan: '100% eléctrico · Carga rápida 800V',
      cayenne: 'SUV · 348 hp · Tracción integral',
      macan: 'SUV compacto · 261 hp',
      panamera: 'Sedán deportivo · 348 hp',
      '911-certificado': 'Pre-Owned Certificado · 12.400 mi',
    },

    badges: {
      'New Arrival': 'Recién llegado',
      Electric: 'Eléctrico',
      Certified: 'Certificado',
      Oferta: 'Oferta',
    },

    service: {
      eyebrow: 'Postventa',
      title: 'Centro de Servicio Técnico Certificado',
      caption: 'Taller Certificado — North Miami',
      imageAlt: 'Técnico certificado trabajando en un Porsche dentro del taller',
      cta: 'Agendar Servicio',
      highlights: [
        {
          title: 'Técnicos certificados por Porsche',
          description:
            'Diagnóstico con equipo PIWIS y partes originales, con garantía de fábrica en cada intervención.',
        },
        {
          title: 'Servicio express y vehículo cortesía',
          description:
            'Mantenimiento programado en el día, con recogida y entrega en el condado de Miami-Dade.',
        },
      ],
      banner: {
        eyebrow: 'Taller certificado',
        title: 'Agendar Servicio Técnico',
        description:
          'Elige día y hora en línea, con vehículo cortesía sujeto a disponibilidad y diagnóstico PIWIS incluido.',
        cta: 'Agendar Servicio Técnico',
      },
    },

    finance: {
      eyebrow: 'Financiamiento',
      title: 'Solicita financiamiento o valúa tu Trade-In',
      body: 'Nuestro equipo estructura tu plan con Porsche Financial Services y te entrega una oferta por tu vehículo actual antes de que visites el showroom.',
      imageAlt: 'Sala de exhibición del concesionario al atardecer',
      steps: [
        { label: 'Solicitud en línea', detail: 'Respuesta en menos de 24 horas hábiles.' },
        { label: 'Valuación de tu Trade-In', detail: 'Oferta en firme, válida por 7 días.' },
        { label: 'Planes Porsche Financial', detail: 'Leasing, financiamiento y balloon.' },
      ],
      ctaPrimary: 'Solicitar Financiamiento',
      ctaSecondary: 'Valuar mi Trade-In',
      banner: {
        eyebrow: 'Porsche Financial Services',
        title: 'Calcular Cuota de Lease',
        description:
          'Simula plazo, enganche y millaje anual para conocer tu cuota mensual estimada en minutos.',
        cta: 'Calcular Cuota de Lease',
      },
    },

    lead: {
      advisorEyebrow: 'Tu asesora',
      advisorRole: 'Asesora de Ventas Certificada Porsche',
      advisorTagline:
        'Más de 10 años acompañando a clientes del sur de la Florida en la compra, leasing y trade-in de su Porsche.',
      responseTime: 'Respuesta promedio en menos de 15 minutos',
      whatsapp: 'Escribir por WhatsApp',
      eyebrow: 'Contacto',
      title: 'Solicita tu cotización personalizada',
      subtitle: (first: string) =>
        `Cuéntanos qué modelo te interesa y ${first} te envía disponibilidad, precio final y opciones de leasing el mismo día hábil.`,
      name: 'Nombre completo',
      namePlaceholder: 'Nombre y apellido',
      phone: 'Teléfono',
      email: 'Correo electrónico',
      emailPlaceholder: 'tunombre@email.com',
      interest: 'Modelo de interés',
      timeline: '¿Cuándo planeas comprar?',
      message: 'Mensaje (opcional)',
      messagePlaceholder: 'Color, paquete de equipamiento, trade-in, prueba de manejo…',
      submit: 'Solicitar Cotización',
      sending: 'Enviando…',
      consent: (name: string, phone: string, email: string) =>
        `Al enviar aceptas ser contactado por ${name} en ${phone} o ${email}. Sin compromiso de compra.`,
      success: (first: string) =>
        `Gracias, tu solicitud fue enviada. ${first} te contactará en breve.`,
      honeypot: 'No llenar',
      interests: [
        'Porsche 911 Carrera',
        'Porsche Taycan',
        'Porsche Cayenne',
        'Porsche Macan',
        'Porsche Panamera',
        'Pre-Owned Certificado',
        'Servicio y Partes',
      ],
      timelines: ['Lo antes posible', 'En 30 días', 'En 1 a 3 meses', 'Solo explorando'],
      whatsappIntro: 'Hola, quiero solicitar una cotización.',
      whatsappFields: {
        name: 'Nombre',
        phone: 'Teléfono',
        email: 'Email',
        interest: 'Modelo de interés',
        timeline: 'Plazo de compra',
        message: 'Mensaje',
      },
    },

    footer: {
      addressLabel: 'Dirección',
      directions: 'Cómo llegar',
      hoursLabel: 'Horarios',
      phoneLabel: 'Teléfono',
      advisorNote: (name: string, role: string, line: string) =>
        `Atención directa con ${name}, ${role.toLowerCase()}. Línea del concesionario: ${line}.`,
      testDrive: 'Agendar Prueba de Manejo',
      newsletter: 'Newsletter',
      newsletterCopy:
        'Nuevos ingresos, ediciones limitadas e invitaciones a eventos del concesionario.',
      emailPlaceholder: 'Correo electrónico',
      subscribe: 'Suscribir',
      subscribed: 'Listo, te mantendremos informado.',
      follow: 'Síguenos',
      columns: [
        {
          title: 'Ventas',
          links: [
            'Inventario Nuevo',
            'Pre-Owned Certificados',
            'Prueba de Manejo',
            'Ofertas Especiales',
          ],
        },
        {
          title: 'Postventa',
          links: ['Agendar Servicio', 'Partes Originales', 'Colisión y Pintura', 'Tequipment'],
        },
        {
          title: 'Concesionario',
          links: ['Sobre Nosotros', 'Financiamiento', 'Trade-In', 'Aviso de Privacidad'],
        },
      ],
      legal: ['Aviso de Privacidad', 'Términos de Uso', 'Accesibilidad', 'Cookies'],
      rights: 'Concesionario oficial autorizado.',
      hours: [
        { days: 'Ventas · Lun – Vie', time: '9:00 AM – 7:00 PM' },
        { days: 'Ventas · Sábado', time: '9:00 AM – 6:00 PM' },
        { days: 'Servicio · Lun – Vie', time: '7:30 AM – 6:00 PM' },
        { days: 'Domingo', time: 'Cerrado' },
      ],
    },
  },
}

export type Copy = (typeof copy)['en']

/** Idioma por defecto: inglés, salvo que el navegador esté en español. */
export function detectLang(): Lang {
  if (typeof window === 'undefined') return 'en'
  const languages = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language]
  return languages.some((code) => code?.toLowerCase().startsWith('es')) ? 'es' : 'en'
}
